/**
 * paytableConfig.js — Paytable overlay configuration and helpers
 *
 * Pure JS module (no React). All device configs, layout ratios,
 * content scaling, helper functions, and module-level texture caches.
 *
 * Consumed by PaytableOverlay.jsx, PaytableSlider.jsx, and slide components.
 */

import { PixiSettings } from "../../../../pixiSettings";
import { symbolData } from "../../../../SymbolData";
import { formatToCustomLocale } from "../../../../utilities";
import { getGameImageSrc } from "../../../../assets";

/**
 * Resolve an image src from the PIXI Assets cache (preloaded during loading screen).
 * Alias for getGameImageSrc — use assetName in config instead of raw img paths.
 */
export const getAssetSrc = getGameImageSrc;

// ---------------------------------------------------------------------------
// Animation timing (matching primary UI pattern)
// ---------------------------------------------------------------------------
export const OVERLAY_FADE_DURATION = 200;
export const CONTENT_ANIM_DURATION = 250;

/**
 * Visible-height gate for the "cramped portrait" paytable/rules branch.
 * Alone this is not enough — fullscreen tall phones can report < 820 CSS px
 * while still having nearly the full screen available to the game.
 */
export const SHORT_PORTRAIT_HEIGHT = 820;

/**
 * Minimum gap (px) between full screen/layout height and game-visible height
 * before we treat the viewport as browser-toolbar-shortened.
 * Fullscreen (game ≈ screen) → gap ~0 → keep standard-mobile layout.
 */
export const BROWSER_CHROME_STOLEN_MIN_PX = 80;

/**
 * True when a standard-mobile portrait viewport is shortened by browser chrome
 * (toolbars), not merely "under SHORT_PORTRAIT_HEIGHT".
 *
 * Compares game-visible height (`dimensions.height` / visualViewport) to the
 * larger of `window.innerHeight` and the screen's long side. Only when the
 * browser clearly stole space AND visible height is still under the short gate
 * do we route to the small-mobile portrait shell.
 *
 * @param {number} visibleHeight - Game / visualViewport height in CSS px
 * @returns {boolean}
 */
export function isBrowserChromeShortenedPortrait(visibleHeight) {
  if (!(visibleHeight < SHORT_PORTRAIT_HEIGHT)) return false;
  if (typeof window === "undefined") return true;

  const layoutHeight = window.innerHeight || visibleHeight;
  const screenLong = Math.max(
    window.screen?.width ?? 0,
    window.screen?.height ?? 0,
  );
  const fullHeight = Math.max(layoutHeight, screenLong);
  const stolenByBrowserChrome = Math.max(0, fullHeight - visibleHeight);

  return stolenByBrowserChrome >= BROWSER_CHROME_STOLEN_MIN_PX;
}

/**
 * Device/orientation buckets shared by the secondary-UI overlays (paytable,
 * game rules, replay). Single source for the standard/small-mobile + tablet
 * split and the toolbar-shortened-portrait → small fallback, so the overlays
 * can't drift out of lock-step with getPaytableShellLayout's own branching.
 *
 * @param {{device: string, orientation: string, isMobileSmall: boolean,
 *   dimensions: {height: number}}} ctx
 * @returns {{isDesktop: boolean, isPortrait: boolean,
 *   isTabletPortrait: boolean, isTabletLandscape: boolean,
 *   isStandardMobilePortrait: boolean, isStandardMobileLandscape: boolean,
 *   isSmallMobilePortrait: boolean, isSmallMobileLandscape: boolean}}
 */
export const getOverlayDeviceBuckets = ({
  device,
  orientation,
  isMobileSmall,
  dimensions,
}) => {
  const isDesktop = device === PixiSettings.DEVICE.DESKTOP;
  const isTablet = device === PixiSettings.DEVICE.TABLET;
  const isPortrait = orientation === PixiSettings.ORIENTATION.PORTRAIT;
  const isStandardMobile =
    device === PixiSettings.DEVICE.MOBILE && !isMobileSmall;
  // Toolbar/browser-chrome-shortened standard portrait falls back to the
  // small-mobile-portrait bucket so content isn't cut off.
  const isShortStandardPortrait =
    isStandardMobile &&
    isPortrait &&
    isBrowserChromeShortenedPortrait(dimensions.height);
  return {
    isDesktop,
    isPortrait,
    isTabletPortrait: isTablet && isPortrait,
    isTabletLandscape: isTablet && !isPortrait,
    isStandardMobilePortrait:
      isStandardMobile && isPortrait && !isShortStandardPortrait,
    isStandardMobileLandscape: isStandardMobile && !isPortrait,
    isSmallMobilePortrait:
      isPortrait && (isMobileSmall || isShortStandardPortrait),
    isSmallMobileLandscape: isMobileSmall && !isPortrait,
  };
};

// Navigation throttle — prevents rapid-fire from automated tools or accessibility
export const NAV_THROTTLE_MS = 100;

// Debug wireframe colors — one per slide so positions are visible
export const DEBUG_SLIDE_COLORS = [
  "#ff0000",
  "#0088ff",
  "#00cc00",
  "#ffdd00",
  "#ff00ff",
  "#00cccc",
  "#ff8800",
  "#88ff00",
  "#ff4488",
  "#ffffff",
];

// ---------------------------------------------------------------------------
// Slide content data
// ---------------------------------------------------------------------------
// The slide ORDER is this array: the overlay renders each entry by its `id`,
// and the count, the dots and the wrap all come off its length.
export const SLIDE_IDS = Object.freeze({
  WILD_SCATTERS: "wildScatters",
  SYMBOLS: "symbols",
  EXPANDING_WILD: "expandingWild",
  PAYLINES: "paylines",
});

export const PAYTABLE_SLIDES = [
  { id: SLIDE_IDS.WILD_SCATTERS, title: "paytable_wild_scatters_title" },
  { id: SLIDE_IDS.SYMBOLS, title: "paytable_symbols_title" },
  {
    id: SLIDE_IDS.EXPANDING_WILD,
    title: "paytable_features_title",
    featureIndex: 0,
  },
  { id: SLIDE_IDS.PAYLINES, title: "paytable_paylines_title" },
];

export const TOTAL_SLIDES = PAYTABLE_SLIDES.length;

/**
 * How wide the DESCRIPTION may run in the landscape column, as a percent of the
 * slide body.
 *
 * The row layout constrained it for free — `.spt-feature-text-wrap` is 50% of
 * the slide, so the text could never span more than half. The column has no
 * such wrapper, so without a cap the description runs the full width and the
 * slide reads as one long line of text under a small picture.
 *
 * Read in TWO places and they must agree: the CSS `max-width` that draws it,
 * and the fit-to-height measurement that picks the font size. Measure a wider
 * box than you draw and the text is sized for room it does not have.
 *
 * Landscape column only — portrait already fills its width by design.
 */
export const STACKED_LANDSCAPE_DESC_MAX_W_PCT = 68;

export const SLIDE_CONTENT_SPACED_GAP_MUL = {
  mobilePortrait: 3,
  mobileSmallPortrait: 3,
  mobileLandscape: 1,
  default: 4,
};

// Standard mobile landscape — uniform feature subtitle + description sizing (all slides)
export const STANDARD_MOBILE_LANDSCAPE_TYPO = {
  featureSubtitleMul: 1.35,
  descFontMul: 1.1,
  fixedDescFont: true,
  // Small mobile landscape — extra shrink on feature-slide descriptions so
  // the longest translations still fit (11px → 10px at this breakpoint).
  smallMobileLandscapeDescFontMul: 0.9,
};

// Small mobile portrait — uniform description + subtitle bumps
export const SMALL_MOBILE_PORTRAIT_TYPO = {
  descFontMul: 13 / 12, // 12px → 13px at typical base
  subtitleFontMul: 16 / 15, // 15px → 16px at typical base
};


export const FEATURE_SLIDES = [
  {
    subtitle: "paytable_features_expanding_wild_title",
    symbolName: "wild",
    // Long translations can push this title past its wrap's width in
    // landscape — keep it on one line and don't clip it.
    subtitleNoWrap: true,
    imgWidth: {
      mobilePortrait: "90%",
      mobileLandscape: "70%",
      desktop: "70%",
    },
    descFontMul: 0.8,
    descWidthMultipliers: [19, 16, 18],
    textPadMul: 0.5,
    landscapeImgMul: 1.3,
    // `mobileLandscape` is EVERY non-desktop landscape, phones and tablets.
    imgMarginTop: { mobileLandscape: "0%" },
    landscapeBodyGapMul: 0.8,
    // 1.5x the shared linePad between title, image and description, portrait
    // only. linePad derives from the title size, so the air scales with it.
    portraitBodyGapMul: 1.5,
    desc: "paytable_features_expanding_wild_description",
  },
];

// Wild & Scatters slide sizing.
export const SPECIAL_SYMBOL_CONFIG = {
  descFontMul: 0.8,
  // Applied in two places that must agree — the fit that decides how much
  // height the images claim, and the render that draws them.
  imgScaleMul: 0.81,
  // Per-section fraction (sections stack in portrait).
  imgFraction: { portrait: 0.5, landscape: 0.38 },
  // Uniform image bump on standard mobile landscape — per-symbol imgScale ratios unchanged
  standardMobileLandscapeImgFractionMul: 1.18,
  // Line box of a pay-value line per px of font — matches .spt-grid-pv.
  payLineHeight: 1.3,
};

// Pictures come from the symbol spines (SpineTextureExtractor, keyed by the
// SymbolData `texture` name). `showPays` cards list the server pay values.
// The two scatters share one description, shown under each of them.
export const SPECIAL_SYMBOL_CARDS = [
  {
    subtitle: "paytable_symbols_wild_title",
    symbolName: "wild",
    imgScale: { mobilePortrait: 1.0, mobileLandscape: 1.0, desktop: 0.6 },
    desc: "paytable_symbols_wild_description",
  },
  {
    subtitle: "paytable_symbols_sun_title",
    symbolName: "sc1",
    showPays: true,
    imgScale: { mobilePortrait: 1.0, mobileLandscape: 1.0, desktop: 0.6 },
    desc: "paytable_symbols_scatter_description",
  },
  {
    subtitle: "paytable_symbols_moon_title",
    symbolName: "sc2",
    showPays: true,
    imgScale: { mobilePortrait: 1.0, mobileLandscape: 1.0, desktop: 0.6 },
    desc: "paytable_symbols_scatter_description",
  },
];

// Symbols slide rows, by SymbolData name. mp1 pays like the low symbols on
// this math, so it leads the low row (as in Noble Fruits).
export const SYMBOL_GRID_ROWS = {
  top: ["hp1", "mp2", "mp3"],
  low: ["mp1", "lp1", "lp2", "lp3", "lp4"],
};

export const SYMBOL_GRID_CONFIG = {
  // Small mobile landscape needs a slight uniform shrink to avoid bottom-row clip
  smallMobileLandscapeScaleMul: 0.92,
  landscapeImgHeightMul: 0.72,
  landscapeFillScaleCap: 1.3,
  portraitPvFontSizeOffset: 1,
  // How wide a landscape row may be, as a percent of the slide. Read by the
  // CSS and by the width fit, so the fit sizes cells for the box they get.
  landscapeRowMaxWidthPct: 82,
};

// ---------------------------------------------------------------------------
// Chrome sizing — overlay shell elements
// ---------------------------------------------------------------------------
// Follows LogoLandscape.jsx DEVICE_CONFIG pattern.
// All ratios are multiples of `chromeBase`.
export const BASE_FONT_SIZE = 28;

export const CHROME_DEVICE_CONFIG = {
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      fontMultiplier: 1.55,
      arrowScale: 1.3,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      fontMultiplier: 1.2,
      arrowScale: 1.3,
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      fontMultiplier: 1.95,
      arrowScale: 1.5,
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      fontMultiplier: 1.5,
      arrowScale: 1.5,
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: { fontMultiplier: 1.4, arrowScale: 1.35 },
};

export const CHROME_RATIOS = {
  title: 2.0, // "Paytable" header h1
  subtitle: 1.5, // "Symbols" / "Feature" per-slide heading
  arrow: 3.3, // left/right chevron icons
  close: 1.25, // close button icon
  dot: 0.4, // pagination dot diameter
  separatorHeight: 0.14, // yellow separator line thickness
  topPad: 0.75, // top padding as ratio of chrome.base (scales with uiSymbolScale)
};

// Fixed carousel arrow layout — shared by PaytableSlider + PaytableOverlay slide content
export const PAYTABLE_ARROW = {
  insetMul: 0.65,
  iconWidthMul: 0.53,
  iconHeightMul: 0.8,
};

export const getPaytableArrowLayout = (chrome) => {
  const inset = Math.round(chrome.base * PAYTABLE_ARROW.insetMul);
  const iconWidth = Math.round(chrome.arrow * PAYTABLE_ARROW.iconWidthMul);
  const iconHeight = Math.round(chrome.arrow * PAYTABLE_ARROW.iconHeightMul);
  const padding = Math.round(chrome.base * 0.4);
  const safeGutter = inset + iconWidth + padding;
  return { inset, iconWidth, iconHeight, safeGutter };
};

// Tuned mobile landscape — shared chrome sizing (header, close, carousel arrows)
export const PAYTABLE_TUNED_LANDSCAPE_CHROME = {
  arrowIconMul: 0.82,
  sideSpreadMul: 2.6,
  headerTitleMul: 1.1,
  slideTitleMul: 1.15,
  // Desktop — header title + slide subtitle near the separator run a touch
  // smaller than the shared mobile-landscape muls.
  desktopHeaderTitleMul: 1.1,
  desktopSlideTitleMul: 1.1,
  // Wide desktop (≥ WIDE_DESKTOP_MIN_WIDTH, 2540px) — starts identical to
  // desktop; tune these independently to adjust sizes/paddings on large
  // monitors without affecting standard desktop.
  wideDesktopHeaderTitleMul: 1.3,
  wideDesktopSlideTitleMul: 1.3,
  wideDesktopShellTopPadMul: 1.7,
  wideDesktopShellBottomPadMul: 3.9,
  wideDesktopSeparatorMul: 1.0,
  wideDesktopSlideContentSpacedGapMul: 1.0,
  // Extra multiplier on the carousel arrow icon for wide desktop only.
  wideDesktopArrowMul: 1.185,
  // Extra downward nudge for the carousel arrows on wide desktop, as a % of
  // viewport height (added to the arrow's `top: calc(50% + …)`).
  wideDesktopArrowDownPct: 0.6,
  closeMul: 1.2,
  // Content width — a fixed multiple of uiSymbolSize per tier. Since the symbol
  // size is already fit to the viewport, this scales continuously and never
  // overflows, so no absolute cap is needed. Higher = wider slides. Seeded to
  // reproduce the previous absolute widths at reference resolutions; tune per
  // tier from the live values.
  standardMobileLandscapeContentWidthSymbolMul: 5.85,
  smallMobileLandscapeContentWidthSymbolMul: 6.4,
  tabletLandscapeContentWidthSymbolMul: 7.1,
  desktopContentWidthSymbolMul: 5.75,
  wideDesktopContentWidthSymbolMul: 7.15,
};

export const getPaytableTunedLandscapeSlideAreaPad = (
  viewportWidth,
  contentWidth,
) => Math.max(0, Math.round((viewportWidth - contentWidth) / 2));

export const getPaytableTunedLandscapeSideOffset = (chrome, slideAreaPad) => {
  const arrowLayout = getPaytableArrowLayout(chrome);
  const arrowIconWidth = Math.round(
    arrowLayout.iconWidth * PAYTABLE_TUNED_LANDSCAPE_CHROME.arrowIconMul,
  );
  const sideSpread = Math.round(
    chrome.base * PAYTABLE_TUNED_LANDSCAPE_CHROME.sideSpreadMul,
  );
  return Math.max(4, slideAreaPad - arrowIconWidth - sideSpread);
};

/**
 * Full overlay-shell layout — pure port of the equivalent computations in
 * PaytableOverlay.jsx (shellTopPad / shellBottomPad / shellContentWidth /
 * closeButton* / headerTitleFontSize / headerPaddingBottom).
 *
 * Exists so a sibling overlay (e.g. ReplayOverlay) can reproduce the paytable
 * shell — identical top/bottom padding, content width, and close-button
 * size/position — without re-deriving the branch tree and drifting out of sync.
 *
 * NOTE: headerTitleFontSize matters even for an overlay with no visible title:
 * the close button is absolutely positioned at top:50% of the header title row,
 * and that row's height is the title's line box. Reproduce the row height or the
 * X lands at a different Y than the paytable's.
 *
 * If PaytableOverlay's formulas change, mirror them here.
 */
export const getPaytableShellLayout = ({
  device,
  orientation,
  dimensions,
  uiSymbolScale,
  uiSymbolSize,
  isMobileSmall,
  isWideDesktop,
}) => {
  const isDesktop = device === PixiSettings.DEVICE.DESKTOP;
  const layoutDevice = isDesktop
    ? PixiSettings.DEVICE.MOBILE
    : getPaytableLayoutDevice(device);
  const isPortrait =
    (device === PixiSettings.DEVICE.MOBILE ||
      device === PixiSettings.DEVICE.TABLET) &&
    orientation === PixiSettings.ORIENTATION.PORTRAIT;

  const config = getDeviceConfig(CHROME_DEVICE_CONFIG, layoutDevice, orientation);
  const base = BASE_FONT_SIZE * config.fontMultiplier * uiSymbolScale;
  const chrome = {
    title: Math.round(base * CHROME_RATIOS.title),
    arrow: Math.round(base * CHROME_RATIOS.arrow * (config.arrowScale || 1.0)),
    close: Math.round(base * CHROME_RATIOS.close),
    topPad: Math.round(base * CHROME_RATIOS.topPad),
    base,
  };

  const isTablet = device === PixiSettings.DEVICE.TABLET;
  const isStandardMobile = device === PixiSettings.DEVICE.MOBILE && !isMobileSmall;
  const isShortStandardPortrait =
    isStandardMobile &&
    isPortrait &&
    isBrowserChromeShortenedPortrait(dimensions.height);
  const isStandardMobilePortrait =
    isStandardMobile && isPortrait && !isShortStandardPortrait;
  const isStandardMobileLandscape = isStandardMobile && !isPortrait;
  const isTabletPortrait = isTablet && isPortrait;
  const isTabletLandscape = isTablet && !isPortrait;
  const isSmallMobilePortrait =
    isPortrait && (isMobileSmall || isShortStandardPortrait);
  const isSmallMobileLandscape = isMobileSmall && !isPortrait;
  const isTunedStandardPortrait = isStandardMobilePortrait || isTabletPortrait;
  const isMirroredMobileLandscape =
    isStandardMobileLandscape || isTabletLandscape || isDesktop;
  const isTunedMobileLandscape =
    isMirroredMobileLandscape || isSmallMobileLandscape;
  const isTunedShellPortrait = isTunedStandardPortrait || isSmallMobilePortrait;

  const shellTopPad = isStandardMobilePortrait
    ? chrome.topPad * 7.5
    : isTabletPortrait
      ? chrome.topPad * 0.8
      : isTabletLandscape
        ? chrome.topPad * 1.4 * 6.5
        : isWideDesktop
          ? chrome.topPad *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopShellTopPadMul
          : isDesktop
            ? chrome.topPad * 1.4
            : isTunedMobileLandscape
            ? chrome.topPad * 1.6
            : isSmallMobilePortrait
              ? chrome.topPad * 0.8
              : Math.round(chrome.topPad * 1.3);

  const shellBottomPad = isStandardMobilePortrait
    ? chrome.topPad * 7.75
    : isTabletPortrait
      ? chrome.topPad * 1.5
      : isTabletLandscape
        ? chrome.topPad * 3.3 * 3.35
        : isWideDesktop
          ? chrome.topPad *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopShellBottomPadMul
          : isTunedMobileLandscape
          ? chrome.topPad * 3.3
          : isSmallMobilePortrait
            ? chrome.topPad * 1.5
            : isPortrait
              ? chrome.topPad * 3.8
              : chrome.topPad * 3.2;

  const contentWidthSymbolMul = isWideDesktop
    ? PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopContentWidthSymbolMul
    : isDesktop
    ? PAYTABLE_TUNED_LANDSCAPE_CHROME.desktopContentWidthSymbolMul
    : isSmallMobileLandscape
      ? PAYTABLE_TUNED_LANDSCAPE_CHROME.smallMobileLandscapeContentWidthSymbolMul
      : isTabletLandscape
        ? PAYTABLE_TUNED_LANDSCAPE_CHROME.tabletLandscapeContentWidthSymbolMul
        : PAYTABLE_TUNED_LANDSCAPE_CHROME.standardMobileLandscapeContentWidthSymbolMul;
  const tunedMobileLandscapeShellWidth = isTunedMobileLandscape
    ? Math.round(uiSymbolSize * contentWidthSymbolMul)
    : null;

  const tabletPortraitContentWidth = isTabletPortrait
    ? dimensions.width * 0.5
    : null;

  const shellContentWidth = isStandardMobilePortrait
    ? dimensions.width * 0.75
    : isTabletPortrait
      ? tabletPortraitContentWidth
      : isSmallMobilePortrait
        ? dimensions.width * 0.8
        : (tunedMobileLandscapeShellWidth ?? undefined);

  const separatorLineWidth = isStandardMobilePortrait
    ? Math.min(chrome.title * 12, dimensions.width * 0.95)
    : isTabletPortrait
      ? tabletPortraitContentWidth
      : isTunedMobileLandscape
        ? tunedMobileLandscapeShellWidth
        : isPortrait
          ? Math.min(chrome.title * 12, dimensions.width * 0.92)
          : Math.min(chrome.title * 16, dimensions.width * 0.65);

  const closeButtonSize = isTunedMobileLandscape
    ? Math.round(chrome.close * PAYTABLE_TUNED_LANDSCAPE_CHROME.closeMul)
    : chrome.close;

  const tunedLandscapeSlideAreaPad =
    isTunedMobileLandscape && shellContentWidth != null
      ? getPaytableTunedLandscapeSlideAreaPad(dimensions.width, shellContentWidth)
      : null;
  const tunedLandscapeChromeSideOffset =
    tunedLandscapeSlideAreaPad != null
      ? getPaytableTunedLandscapeSideOffset(chrome, tunedLandscapeSlideAreaPad)
      : null;

  const closeButtonRight = isPortrait
    ? Math.max(6, Math.round(chrome.close * 0.12))
    : tunedLandscapeChromeSideOffset != null
      ? Math.max(4, tunedLandscapeChromeSideOffset - Math.round(chrome.close * 0.35))
      : shellContentWidth != null
        ? Math.max(
            4,
            Math.round((dimensions.width - shellContentWidth) / 2) -
              Math.round(chrome.close * 0.8),
          )
        : Math.round(
            (dimensions.width - separatorLineWidth) / 2 - chrome.close * 3.0,
          );

  const headerTitleFontSize = isTunedStandardPortrait
    ? Math.round(chrome.title * 0.94)
    : isWideDesktop
      ? Math.round(
          chrome.title *
            PAYTABLE_TUNED_LANDSCAPE_CHROME.wideDesktopHeaderTitleMul,
        )
      : isDesktop
      ? Math.round(
          chrome.title * PAYTABLE_TUNED_LANDSCAPE_CHROME.desktopHeaderTitleMul,
        )
      : isTunedMobileLandscape
        ? Math.round(
            chrome.title * PAYTABLE_TUNED_LANDSCAPE_CHROME.headerTitleMul,
          )
        : chrome.title;

  const linePad = Math.round(chrome.title * 0.5);
  const headerPaddingBottom = isTunedShellPortrait
    ? 0
    : Math.round(linePad * 0.25);

  return {
    shellTopPad,
    shellBottomPad,
    shellContentWidth,
    closeButtonSize,
    closeButtonRight,
    headerTitleFontSize,
    headerPaddingBottom,
  };
};

// ---------------------------------------------------------------------------
// Per-slide content layout
// ---------------------------------------------------------------------------
// Each slide type declares its structure. Actual pixel sizes are computed
// at render time from `contentHeight`.
//
// imageFraction:  portion of card height allocated to the image
// fontToImage:    pay-value font size as fraction of image size
// descToImage:    description text as fraction of image size
// nameToImage:    symbol name label as fraction of image size
// gapFraction:    inter-card gap as fraction of available grid height
export const SLIDE_LAYOUT = {
  // Slide 1: Wild + Scatter — large special symbol cards
  specialSymbols: {
    columns: { portrait: 1, landscape: 2 },
    rows: { portrait: 2, landscape: 1 },
    imageFraction: 0.5,
    fontToImage: 0.13,
    descToImage: 0.11,
    nameToImage: 0.14,
    gapFraction: 0.04,
  },
  // Slide 2: Regular symbols (hp, mp, lp) — grid layout
  regularSymbols: {
    columns: { portrait: 2, landscape: 4 },
    rows: { portrait: 4, landscape: 2 },
    imageFraction: 0.55,
    fontToImage: 0.14,
    gapFraction: 0.03,
  },
  // Slides 3–5: Feature explanation — always row, 50/50 image + text
  feature: {
    fontToImage: 0.08,
  },
  // Slides 6–7: Payline diagrams — single large image
  paylines: {
    imageFraction: 0.9,
  },
};

// ---------------------------------------------------------------------------
// Content max width per device/orientation (fraction of viewport width)
// ---------------------------------------------------------------------------
export const CONTENT_WIDTH_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 0.92,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 0.75,
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 0.9,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 0.75,
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: 0.9,
    [PixiSettings.ORIENTATION.LANDSCAPE]: 0.92,
  },
  [PixiSettings.DEVICE.DESKTOP]: 0.88,
};

// ---------------------------------------------------------------------------
// Description font sizing — per-device/orientation ratios
// baseMul: base font multiplier (chrome.base * baseMul)
// ratio:   scales both height and width of text container (1.0 = full, 0.9 = 90%)
// ---------------------------------------------------------------------------
export const DESC_FONT_CONFIG = {
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: { baseMul: 1.0, ratio: 1.0 },
    [PixiSettings.ORIENTATION.LANDSCAPE]: { baseMul: 0.85, ratio: 1.0 },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: { baseMul: 1.0, ratio: 1.0 },
    [PixiSettings.ORIENTATION.LANDSCAPE]: { baseMul: 0.85, ratio: 1.0 },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: { baseMul: 0.85, ratio: 1.0 },
    [PixiSettings.ORIENTATION.LANDSCAPE]: { baseMul: 0.85, ratio: 1.0 },
  },
  [PixiSettings.DEVICE.DESKTOP]: { baseMul: 0.95, ratio: 0.75 },
};

// ---------------------------------------------------------------------------
// Global description font multiplier — layered ON TOP of each slide's own
// descFontMul, applied to the STARTING font size fed into each slide's
// auto-fit measurement (the shrink-to-fit loop for feature slides, the
// design-font input to the ratio-scale for Wild/Coin/Zeus slides). A language
// that needs the full shrink still gets it — this only lets a locale/tier
// that leaves slack render a touch bigger before that fit math runs.
// Applies to every slide with a description EXCEPT the Regular Grid (slide 3,
// pay-value numbers — a separate, unrelated font path) and the Paylines
// slide (image only, no text). Keep values close to 1.0 — this should read
// as a 1-2px nudge, never a visible jump.
// ---------------------------------------------------------------------------
export const GLOBAL_DESC_FONT_MULTIPLIER = {
  default: {
    mobileSmallPortrait: 1.0,
    mobileSmallLandscape: 1.0,
    mobilePortrait: 1.0,
    mobileLandscape: 1.0,
    tabletPortrait: 1.0,
    tabletLandscape: 1.0,
    desktop: 1.0,
    wideDesktop: 1.0,
  },
  // Locale-specific overrides, keyed by i18n language code (e.g. "ru").
  // Falls back to `default` for any locale or tier not listed here.
  ru: {
    mobileSmallPortrait: 1.065,
    mobileSmallLandscape: 1.1,
    mobilePortrait: 1.065,
    mobileLandscape: 1.05,
    desktop: 1.1,
    wideDesktop: 1.2,
  },
  en: {
    mobileSmallPortrait: 1.065,
    mobileSmallLandscape: 1.1,
    mobilePortrait: 1.065,
    mobileLandscape: 1.05,
    desktop: 1.1,
    wideDesktop: 1.2,
  },
};

/**
 * Resolve the global description font multiplier for a device/orientation
 * tier + locale. Tier keys mirror PaytableOverlay's own device/orientation
 * booleans exactly — no separate small/wide desktop split beyond what that
 * file already computes via isWideDesktop.
 * @param {string} tierKey - one of: mobileSmallPortrait, mobileSmallLandscape,
 *   mobilePortrait, mobileLandscape, tabletPortrait, tabletLandscape, desktop, wideDesktop
 * @param {string} [locale] - i18n language code (e.g. "ru")
 * @returns {number} multiplier, defaults to 1.0 if unset
 */
export const getGlobalDescFontMultiplier = (tierKey, locale) => {
  const localeConfig = locale && GLOBAL_DESC_FONT_MULTIPLIER[locale];
  return (
    localeConfig?.[tierKey] ??
    GLOBAL_DESC_FONT_MULTIPLIER.default[tierKey] ??
    1.0
  );
};

// ---------------------------------------------------------------------------
// Per-slide content scale (fraction of available width/height)
// ---------------------------------------------------------------------------
// Fine-tune how much of the content area each slide type uses per device.
export const SLIDE_CONTENT_SCALE = {
  specialSymbols: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 0.95, h: 0.95 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 0.9, h: 0.9 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 0.95, h: 0.95 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 0.85, h: 0.9 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 0.85, h: 0.9 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 0.75, h: 0.85 },
    },
    [PixiSettings.DEVICE.DESKTOP]: { w: 0.65, h: 0.8 },
  },
  regularSymbols: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 1.0, h: 1.0 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 1.0, h: 1.0 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 0.95, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 0.9, h: 1.0 },
    },
    [PixiSettings.DEVICE.DESKTOP]: { w: 0.85, h: 1.0 },
  },
  feature: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 0.85, h: 1.0 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 0.85, h: 1.0 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 0.95, h: 1.0 },
    },
    [PixiSettings.DEVICE.DESKTOP]: { w: 0.9, h: 1.0 },
  },
  paylines: {
    [PixiSettings.DEVICE.MOBILE_SMALL]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 1.0, h: 1.0 },
    },
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 1.0, h: 1.0 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { w: 1.0, h: 1.0 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { w: 1.0, h: 1.0 },
    },
    [PixiSettings.DEVICE.DESKTOP]: { w: 0.75, h: 1.0 },
  },
};

// ---------------------------------------------------------------------------
// Game Rules overlay — padding layout (AutoplayPopup fullscreen pattern)
// ---------------------------------------------------------------------------
export const RULES_OVERLAY_PADDING_CONFIG = {
  basePadding: { horizontal: 28, vertical: 40 },
  [PixiSettings.DEVICE.MOBILE_SMALL]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 1.4, top: 1.5, bottom: 0.5 },
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.8, top: 2.6, bottom: 0.75 },
    },
  },
  [PixiSettings.DEVICE.MOBILE]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 1.4, v: 6.75 },
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 5.0, top: 2.75, bottom: 0.75 },
    },
  },
  [PixiSettings.DEVICE.TABLET]: {
    [PixiSettings.ORIENTATION.PORTRAIT]: {
      paddingMultiplier: { h: 8.0, v: 7.25 },
    },
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.5, v: 0.75 },
    },
  },
  [PixiSettings.DEVICE.DESKTOP]: {
    [PixiSettings.ORIENTATION.LANDSCAPE]: {
      paddingMultiplier: { h: 1.5, v: 0.75 },
    },
  },
};

/**
 * Resolve rules overlay content box from padding multipliers.
 * Mirrors AutoplayPopup fullscreen viewportConfig math.
 */
export const resolveRulesLayout = ({
  dimensions,
  toolbarWidth,
  device,
  orientation,
  isMobileSmall,
  uiSymbolScale,
  headerHeight = 0,
}) => {
  const configDevice = isMobileSmall
    ? PixiSettings.DEVICE.MOBILE_SMALL
    : device;
  const tierConfig = getDeviceConfig(
    RULES_OVERLAY_PADDING_CONFIG,
    configDevice,
    orientation,
  );
  const multipliers = tierConfig?.paddingMultiplier || { h: 1.0, v: 1.0 };

  const isSmallMobileLandscape =
    isMobileSmall && orientation === PixiSettings.ORIENTATION.LANDSCAPE;
  const containerWidth = isSmallMobileLandscape
    ? dimensions.width
    : Math.min(dimensions.width, toolbarWidth || dimensions.width);

  const paddingH = Math.round(
    RULES_OVERLAY_PADDING_CONFIG.basePadding.horizontal *
      multipliers.h *
      uiSymbolScale,
  );
  const topMult = multipliers.top ?? multipliers.v ?? 1.0;
  const bottomMult = multipliers.bottom ?? multipliers.v ?? 1.0;
  const paddingTop = Math.round(
    RULES_OVERLAY_PADDING_CONFIG.basePadding.vertical * topMult * uiSymbolScale,
  );
  const paddingBottom = Math.round(
    RULES_OVERLAY_PADDING_CONFIG.basePadding.vertical *
      bottomMult *
      uiSymbolScale,
  );

  const contentWidth = containerWidth - paddingH * 2;
  const contentHeight = dimensions.height - paddingTop - paddingBottom;
  const availableTextHeight = Math.max(0, contentHeight - headerHeight);

  const tierLabel = `${configDevice}_${orientation}`;

  return {
    tierLabel,
    paddingH,
    paddingTop,
    paddingBottom,
    containerWidth,
    contentWidth,
    contentHeight,
    availableTextHeight,
  };
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Paytable layout treats tablet as mobile — chrome/content scale via uiSymbolScale.
 */
export const getPaytableLayoutDevice = (device) =>
  device === PixiSettings.DEVICE.TABLET ? PixiSettings.DEVICE.MOBILE : device;

/**
 * Resolve device config — Desktop has no orientation nesting.
 * Falls back to Desktop config if device/orientation combo not found.
 */
export const getDeviceConfig = (configObj, device, orientation) => {
  const layoutDevice = getPaytableLayoutDevice(device);
  if (layoutDevice === PixiSettings.DEVICE.DESKTOP) {
    return configObj[layoutDevice] || configObj[PixiSettings.DEVICE.DESKTOP];
  }
  return (
    configObj[layoutDevice]?.[orientation] ||
    configObj[PixiSettings.DEVICE.DESKTOP]
  );
};

/**
 * Resolve per-device/orientation image width from a widthMap.
 */
/**
 * ── THREE KEYS. That is the whole contract ──
 *
 *   mobilePortrait   every portrait device, phone AND tablet
 *   desktop          desktop only
 *   mobileLandscape  every other landscape, phone AND tablet
 *
 * `getPaytableLayoutDevice` folds TABLET into MOBILE, so a `tabletPortrait` or
 * `tabletLandscape` key in a map read through here is never looked at. Sixteen
 * of them were removed on 2026-09-09 — they looked like tuned values and had
 * never been drawn once, which is worse than no value at all.
 *
 * Not every tablet key in this file is dead: the global description-font tiers
 * are resolved by `descFontTierKey` in PaytableOverlay, which DOES distinguish
 * tablet. Check which resolver reads a map before adding or removing a key.
 */
export const getImgWidth = (widthMap, device, isPortrait) => {
  if (!widthMap) return undefined;
  const layoutDevice = getPaytableLayoutDevice(device);
  const isDesktop = layoutDevice === PixiSettings.DEVICE.DESKTOP;
  if (isPortrait) {
    return widthMap.mobilePortrait;
  }
  if (isDesktop) return widthMap.desktop;
  return widthMap.mobileLandscape;
};

/**
 * Reusable off-screen canvas for text measurement (avoids creating new ones).
 */
let _measureCanvas = null;
export const getMeasureCanvas = () => {
  if (!_measureCanvas) _measureCanvas = document.createElement("canvas");
  return _measureCanvas;
};

/**
 * Measure wrapped text height using canvas text measurement.
 * Shared by all slide sizing computations to avoid duplication.
 */
export const measureTextHeight = (ctx, text, fontSize, maxWidth) => {
  ctx.font = `400 ${fontSize}px 'Noto Sans', sans-serif`;
  const lineHeight = fontSize * 1.4;

  const measureWrappedSegment = (segment) => {
    if (!segment) return 1;

    const words = segment.split(/\s+/).filter(Boolean);
    if (words.length === 0) return 1;

    if (words.length === 1) {
      let lines = 1;
      let lineW = 0;
      for (const char of segment) {
        const charW = ctx.measureText(char).width;
        if (lineW + charW > maxWidth && lineW > 0) {
          lines++;
          lineW = charW;
        } else {
          lineW += charW;
        }
      }
      return lines;
    }

    let lines = 1;
    let lineW = 0;
    for (const word of words) {
      const wordW = ctx.measureText(`${word} `).width;
      if (lineW + wordW > maxWidth && lineW > 0) {
        lines++;
        lineW = wordW;
      } else {
        lineW += wordW;
      }
    }
    return lines;
  };

  let totalLines = 0;
  for (const segment of text.split("\n")) {
    totalLines += measureWrappedSegment(segment);
  }
  return totalLines * lineHeight;
};

/**
 * Group symbols by pay category (wild, scatter, hp, mp, lp).
 */
export const getSymbolGroups = () => {
  const groups = { wild: [], scatter: [], hp: [], mp: [], lp: [] };
  Object.entries(symbolData).forEach(([id, data]) => {
    const numId = parseInt(id);
    if (data.symbolPay === "wild") groups.wild.push({ id: numId, ...data });
    else if (data.symbolPay === "scatter")
      groups.scatter.push({ id: numId, ...data });
    else if (data.symbolPay === "hp") groups.hp.push({ id: numId, ...data });
    else if (data.symbolPay === "mp") groups.mp.push({ id: numId, ...data });
    else if (data.symbolPay === "lp") groups.lp.push({ id: numId, ...data });
  });
  return groups;
};

export const getSymbolIdByName = (name) => {
  const entry = Object.entries(symbolData).find(([, d]) => d.name === name);
  return entry ? parseInt(entry[0], 10) : null;
};

/**
 * Symbols-slide rows from SYMBOL_GRID_ROWS, resolved against SymbolData.
 * Unknown names are dropped.
 * @returns {{top: Array<object>, low: Array<object>}}
 */
export const getSymbolGridRows = (rows = SYMBOL_GRID_ROWS) => {
  const resolve = (names) =>
    names
      .map((name) => {
        const id = getSymbolIdByName(name);
        return id == null ? null : { id, ...symbolData[id] };
      })
      .filter(Boolean);
  return { top: resolve(rows.top), low: resolve(rows.low) };
};

/**
 * Portrait split of the symbols grid: two per row, and when the count is odd
 * the last three share one row, so no row holds a single symbol.
 * @param {Array} symbols in display order
 * @returns {{twoCol: Array, threeCol: Array}}
 */
export const splitPortraitGrid = (symbols) => {
  const tail = symbols.length % 2 === 1 ? Math.min(3, symbols.length) : 0;
  const cut = symbols.length - tail;
  return { twoCol: symbols.slice(0, cut), threeCol: symbols.slice(cut) };
};

/**
 * Get formatted pay values for a symbol (descending by count, non-zero only).
 * Matches MiniPaytable: payout = symbolValue × (totalBet / baseBet).
 * baseBet is the API line/unit factor (e.g. 25) — without it values are N× too high.
 */
export const getPayValues = (paytable, symbolId, bet, baseBet = 1) => {
  const payValues = paytable?.[symbolId] || [];
  const safeBaseBet = baseBet || 1;
  const betUnit = (bet || 1) / safeBaseBet;
  return payValues
    .map((value, idx) => ({ value, count: idx + 1 }))
    .reverse()
    .filter((item) => item.value !== 0)
    .map((item) => ({
      count: item.count,
      payout: formatToCustomLocale(betUnit * item.value),
    }));
};

/**
 * Convert a PIXI texture (from Assets cache) to a data URL for use in <img>.
 */
export const pixiTextureToDataURL = (texture) => {
  if (!texture || !texture.baseTexture?.resource?.source) return "";
  try {
    const frame = texture.frame;
    const source = texture.baseTexture.resource.source;
    const canvas = document.createElement("canvas");
    canvas.width = frame.width;
    canvas.height = frame.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      source,
      frame.x,
      frame.y,
      frame.width,
      frame.height,
      0,
      0,
      frame.width,
      frame.height,
    );
    return canvas.toDataURL("image/webp");
  } catch {
    return "";
  }
};

// ---------------------------------------------------------------------------
// Module-level caches for extracted textures (survive re-mounts)
// ---------------------------------------------------------------------------
// Using getter/setter pattern for cleaner module boundaries.

let _cachedSymbolImages = null;
let _cachedFeatureImages = null;
let _cachedPaylineImages = { portrait: null, landscape: null };

export const getCachedSymbolImages = () => _cachedSymbolImages;
export const setCachedSymbolImages = (v) => {
  _cachedSymbolImages = v;
};

export const getCachedFeatureImages = () => _cachedFeatureImages;
export const setCachedFeatureImages = (v) => {
  _cachedFeatureImages = v;
};

export const getCachedPaylineImages = () => _cachedPaylineImages;
export const setCachedPaylineImage = (key, v) => {
  _cachedPaylineImages[key] = v;
};
